import { Instance } from 'mobx-state-tree'
import { Quiz } from '../models/Quiz'
import { Resource } from '../models/Resource'
import { QuizApi } from '../services/api-objects/QuizApi'
import { QuestionHelper } from './QuestionHelper'
import { ResourceHelper } from './ResourceHelper'
import { Question } from '../models/Question'

import { shuffle, groupBy, sortBy } from 'lodash'

let rootStore
export class QuizHelper {
  static setRootStore = (store: any) => {
    rootStore = store
  }

  static importQuizzes = (data, allowEmpty?) => {
    // error out if no quizzes found

    let untitledCount = 0
    data.map((row) => {
      if (!row.quiz) {
        untitledCount++
        row.quiz = `Untitled Quiz ${untitledCount}`
      }
    })

    if (untitledCount === data?.length && !allowEmpty) {
      return []
    }

    const quizzes = groupBy(data, 'quiz')
    return Object.keys(quizzes).map((title) => {
      const questions = QuestionHelper.importQuestions(quizzes[title])
      const hasInfoCards = Boolean(questions.find((q) => q.question_type === 'Card'))
      return this.createNewQuiz({
        title: title.includes('Untitled Quiz') ? '' : title,
        touched: true,
        questions,
        shuffle: !hasInfoCards,
        keep_item_order: hasInfoCards,
      })
    })
  }

  static loadQuiz = async (id: number, init?: any) => {
    let newQuiz: Instance<typeof Quiz> | null = null
    const getQuizResponse = await QuizApi.getQuiz(id)

    const quiz = getQuizResponse.data?.quiz?.pop()
    if (getQuizResponse.ok && quiz) {
      const cards =
        quiz.cards?.map((card) => {
          const cardQuestion = QuestionHelper.createNewQuestion('Card', {
            card_json: card.versions[0].card_json,
            card_name: card.card_name,
            original_name: card.card_name,
            card_id: card.card_id,
            order: card.order,
            approved: true,
            touched: true,
          })
          return cardQuestion
        }) || []
      const questions = [
        ...cards,
        ...(quiz.questions ?? ([] as Array<Instance<typeof Question>>)).map((question, index) => {
          return {
            ...question,
            advanced: Boolean(question.choices.some((choice) => choice.factoid)),
            approved: true,
            keep_answer_order: question.keep_answer_order,
            touched: true,
            id: question.question_id,
            is_locked: false,
            image_src: question.image,
          }
        }),
      ]

      newQuiz = Quiz.create({
        ...quiz,
        cards: [],
        original_name: quiz?.title,
        is_locked: false,
        questions: quiz?.keep_item_order ? sortBy(questions, ['order']) : shuffle(questions),
        ...init,
      })

      if (newQuiz.hasInfoCards) {
        newQuiz.set('keep_item_order', true)
        newQuiz.set('shuffle', false)
      }
    } else {
      throw new Error(`failed to get quiz ${id}`)
    }

    QuizHelper.loadResources(newQuiz).then(() => {
      const unattachedResources = [] as any
      newQuiz?.questions?.map((question) => {
        if (question.resource_id) {
          const found = newQuiz?.resources?.find((r) => r.resource_id === question.resource_id)
          if (!found) {
            unattachedResources.push(question.resource_id)
          }
        }
      })
      newQuiz?.set('removingResources', unattachedResources)
    })

    return newQuiz
  }

  static loadResources = async (quiz: Instance<typeof Quiz>) => {
    if (!quiz.quiz_id) {
      return
    }

    const getQuizResourcesResponse = await QuizApi.getResources(
      false,
      [
        { field: 'source_created_date' },
        { field: 'source_title' },
        { field: 'source_filename_url' },
        { field: 'source_file_size' },
        { field: 'source_file_type' },
        { field: 'source_mimetype' },
        { field: 'quiz_source_required' },
        { field: 'source_processing_status' },
      ],
      'list_quiz_sources',
      quiz.quiz_id,
    )

    if (getQuizResourcesResponse.ok && getQuizResourcesResponse.data?.rows.length) {
      quiz.set(
        'resources',
        ResourceHelper.getQuizResourceToResources(getQuizResourcesResponse.data.rows),
      )
    }
    return getQuizResourcesResponse
  }

  /**
   * returns a new quiz
   */
  static createNewQuiz = (init?: any) => {
    return Quiz.create({
      title: '',
      keep_item_order: false,
      order: null,
      questions: [],
      ...init,
    })
  }

  static updateQuizResourceTitles = (
    fetchedResources: Array<Instance<typeof Resource>>,
    quiz: Instance<typeof Quiz>,
  ) => {
    if (!quiz.resources?.length) {
      return true
    }

    quiz.resources.forEach((r) => {
      const existingResource = fetchedResources.find(
        (fetchedResource) => fetchedResource.source_id === r.source_id,
      )
      if (existingResource) {
        r.set('title', existingResource.title)
      }
    })
  }

  static hasResource = (resourceToFind: Instance<typeof Resource>, quiz: Instance<typeof Quiz>) => {
    if (!quiz.resources?.length) {
      return false
    }

    try {
      return !!quiz.resources.find((r) => r.source_id === resourceToFind.source_id)
    } catch {
      return false
    }
  }

  static publishImages = async (quiz: Instance<typeof Quiz>) => {
    if (!quiz) {
      return
    }

    if (quiz.quiz_id && quiz.imageFile) {
      await QuizApi.publishImage(quiz.quiz_id, quiz.imageFile)
    } else if (quiz.image) {
      // do nothing
    } else if (quiz.quiz_id) {
      await QuizApi.removeImage(quiz.quiz_id)
    }
    if (quiz.questions?.length) {
      quiz.questions.filter((q) => q.question_id).forEach((q) => QuestionHelper.publishImage(q))
    }
  }

  static publishResources = async (quiz: Instance<typeof Quiz>) => {
    if (!quiz.quiz_id) {
      throw new Error(`quiz needs an id to publish resources`)
    }

    if (quiz?.removingResources && quiz?.removingResources.length) {
      await QuizApi.removeResources(quiz.quiz_id, quiz?.removingResources)
    }

    const adding = quiz?.addingResources?.filter((r) => {
      const req = quiz?.requiredResources?.map((r) => r.source_id)
      return !req.includes(r.source_id)
    })

    if (adding && adding?.length) {
      await QuizApi.addResources(quiz.quiz_id!, adding)
    }

    if (quiz.requiredResources && quiz?.requiredResources?.length) {
      await QuizApi.addResources(quiz.quiz_id!, quiz.requiredResources)
    }
  }

  static updateResources = (quiz: Instance<typeof Quiz>, resource: Instance<typeof Resource>) => {
    if (quiz.resources?.length) {
      quiz.resources.forEach((r) => {
        if (r.source_id === resource.source_id) {
          r.set('title', resource.title)
        }
      })
    }
  }
}
